// D04/D06/D07/D12 — digital commerce calculators & simulators.
import { useMemo, useState } from "react";
import { channelCompare, cohortRetention, funnelLeakage, pricingWar, inr, pct } from "./engines";
import { Field, SliderField, ResultRow, ToolSection, Note, useNumbers, copyText } from "./ui";

/* ---------- D04 D2C vs Marketplace Comparator ---------- */
const D04_D2C = { feePct: 0, flatFee: 0, paymentPct: 2, returnsPct: 10, logistics: 80, adPerOrder: 120 };
const D04_MKT = { feePct: 20, flatFee: 30, paymentPct: 0, returnsPct: 15, logistics: 65, adPerOrder: 90 };
const D04_FIELDS = [["feePct", "Commission / fee %"], ["flatFee", "Flat fee per order (₹)"], ["paymentPct", "Payment fee %"], ["returnsPct", "Return rate %"], ["logistics", "Logistics per order (₹)"], ["adPerOrder", "Ads / acquisition per order (₹)"]];
export function ToolD04() {
  const [price, setPrice] = useState(1000);
  const [cost, setCost] = useState(400);
  const [d2c, setD2c] = useState(D04_D2C);
  const [mkt, setMkt] = useState(D04_MKT);
  const r = useMemo(() => channelCompare({ price, productCost: cost, d2c, mkt }), [price, cost, d2c, mkt]);
  const col = (vals, setter, label, keyPrefix) => (
    <div className="border border-zinc-200 p-4">
      <div className="text-sm font-medium mb-3">{label}</div>
      <div className="space-y-3">
        {D04_FIELDS.map(([k, fl]) => (
          <Field key={k} label={fl} value={vals[k]} onChange={(v) => setter((s) => ({ ...s, [k]: v }))} testid={`d04-${keyPrefix}-${k}`} />
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="tool-D04">
      <div className="grid grid-cols-2 gap-4 max-w-md">
        <Field label="Selling price (₹)" value={price} onChange={setPrice} testid="d04-price" />
        <Field label="Product cost (₹)" value={cost} onChange={setCost} testid="d04-cost" />
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {col(d2c, setD2c, "Your D2C website", "d2c")}
        {col(mkt, setMkt, "Marketplace", "mkt")}
      </div>
      <div className="mt-6 max-w-lg" data-testid="d04-result">
        <ResultRow label="D2C contribution / order" value={`${inr(r.d2c.contribution)} (${r.d2c.contributionPct}%)`} strong tone={r.d2c.contribution < 0 ? "bad" : "good"} />
        <ResultRow label="Marketplace contribution / order" value={`${inr(r.marketplace.contribution)} (${r.marketplace.contributionPct}%)`} tone={r.marketplace.contribution < 0 ? "bad" : undefined} />
        <ResultRow label="D2C keeps extra" value={inr(r.delta)} tone={r.delta > 0 ? "good" : "bad"} strong />
      </div>
      {r.flags.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="d04-flags">
          {r.flags.map((f, i) => <Note key={i} tone="warn">{f}</Note>)}
        </div>
      )}
      <Note>Like-for-like means the same product at the same price with complete costs on both sides. Returns are costed at 35% of order value per return event (shipping both ways + handling + resale loss) — disclosed so you can adjust the rate, not the arithmetic.</Note>
    </div>
  );
}

/* ---------- D06 Retention Cohort Analyzer (CSV upload + column mapping) ---------- */
const D06_DEMO = `order_id,customer_email,order_date,amount
1001,asha@example.com,2026-01-05,1499
1002,ravi@example.com,2026-01-08,899
1003,meera@example.com,2026-01-12,2100
1011,imran@example.com,2026-01-20,1240
1004,asha@example.com,2026-02-10,1299
1012,imran@example.com,2026-02-15,980
1005,arjun@example.com,2026-02-02,750
1006,arjun@example.com,2026-02-20,1600
1013,farah@example.com,2026-02-05,1120
1014,kabir@example.com,2026-02-18,860
1007,ravi@example.com,2026-03-15,999
1008,meera@example.com,2026-03-22,1850
1009,sana@example.com,2026-03-28,640
1015,zoya@example.com,2026-03-10,1490
1016,omar@example.com,2026-03-25,720
1017,asha@example.com,2026-04-11,1750`;
function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const splitRow = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === "," && !inQ) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  return { headers: splitRow(lines[0]), rows: lines.slice(1).map(splitRow) };
}
export function ToolD06() {
  const [parsed, setParsed] = useState(null);
  const [map, setMap] = useState({ customer: "", date: "" });
  const [fileName, setFileName] = useState("");
  const result = useMemo(() => {
    if (!parsed || !map.customer || !map.date) return null;
    const ci = parsed.headers.indexOf(map.customer);
    const di = parsed.headers.indexOf(map.date);
    const orders = parsed.rows.filter((r) => r.length > 1).map((r) => ({ customerId: r[ci], dateISO: r[di] }));
    return cohortRetention(orders);
  }, [parsed, map]);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const p = parseCsv(String(reader.result));
      setParsed(p);
      const guess = (cands) => p.headers.find((h) => cands.some((c) => h.toLowerCase().includes(c))) || "";
      setMap({ customer: guess(["email", "customer", "phone", "user"]), date: guess(["date", "created", "ordered"]) });
    };
    reader.readAsText(f);
  };
  const loadDemo = () => {
    const p = parseCsv(D06_DEMO);
    setParsed(p); setFileName("demo-orders.csv");
    setMap({ customer: "customer_email", date: "order_date" });
  };
  return (
    <div data-testid="tool-D06">
      <div className="border border-zinc-200 p-5" data-testid="d06-upload">
        <div className="text-sm font-medium">Upload your orders CSV</div>
        <p className="text-xs text-zinc-500 mt-1">Needs one column identifying the customer (email/phone/ID) and one order-date column. Nothing leaves your browser — parsing is local.</p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="d06-file" />
          <button type="button" onClick={loadDemo} className="text-sm font-medium underline underline-offset-4" data-testid="d06-demo">Load demo data (10 orders, 5 customers)</button>
        </div>
        {parsed && (
          <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="d06-mapper">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">Customer column</span>
              <select value={map.customer} onChange={(e) => setMap((m) => ({ ...m, customer: e.target.value }))} className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="d06-map-customer">
                <option value="">Choose…</option>
                {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">Order date column</span>
              <select value={map.date} onChange={(e) => setMap((m) => ({ ...m, date: e.target.value }))} className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="d06-map-date">
                <option value="">Choose…</option>
                {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
          </div>
        )}
        {fileName && <p className="mt-2 text-xs text-zinc-400">Loaded: {fileName} ({parsed?.rows.length || 0} rows)</p>}
      </div>
      {result && result.cohorts.length > 0 && (
        <div className="mt-8" data-testid="d06-result">
          <ResultRow label="Customers" value={String(result.customers)} />
          <ResultRow label="Valid order rows" value={`${result.parsed} / ${result.total}`} />
          <ResultRow label="Median days to second order" value={result.medianSecondOrderDays === null ? "—" : String(result.medianSecondOrderDays)} strong />
          <div className="mt-6 overflow-x-auto">
            <table className="text-sm" data-testid="d06-heatmap">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-zinc-500">
                  <th className="py-2 pr-4 text-left font-medium">Cohort</th>
                  <th className="py-2 pr-4 text-left font-medium">Size</th>
                  {[0, 1, 2, 3, 4, 5].map((m) => <th key={m} className="py-2 pr-2 font-medium text-center w-16">M+{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.cohorts.map((c) => (
                  <tr key={c.month} data-testid={`d06-cohort-${c.month}`}>
                    <td className="py-1.5 pr-4 font-medium">{c.month}</td>
                    <td className="py-1.5 pr-4 text-zinc-500">{c.size}</td>
                    {c.rates.map((rate, i) => (
                      <td key={i} className="py-1.5 pr-2">
                        {rate === null ? <div className="h-8 bg-zinc-50 text-center text-xs text-zinc-300 leading-8">·</div> : (
                          <div className="h-8 text-center text-xs leading-8 tabular-nums text-white" style={{ backgroundColor: `rgba(0,0,0,${Math.max(0.12, rate / 100)})` }} data-testid={`d06-cell-${c.month}-${i}`}>{rate}%</div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Note>M+0 is the first-purchase month (always 100%). The column that matters is M+1: if fewer than 1 in 5 customers return the next month, you have a product-experience question before you have a marketing budget question.</Note>
        </div>
      )}
      {result && result.error && <Note tone="warn">No valid rows found with those column choices — check the customer and date mapping.</Note>}
    </div>
  );
}

/* ---------- D07 Website Conversion Leak Detector ---------- */
const D07_DEFAULT = [
  { name: "Sessions", count: 50000 },
  { name: "Product page views", count: 21000 },
  { name: "Add to cart", count: 3200 },
  { name: "Checkout started", count: 1900 },
  { name: "Purchases", count: 1250 },
];
export function ToolD07() {
  const [stages, setStages] = useState(D07_DEFAULT);
  const [aov, setAov] = useState(1400);
  const [cm, setCm] = useState(35);
  const r = useMemo(() => funnelLeakage({ stages, avgDealValue: (Number(aov) || 0) * (Number(cm) || 0) / 100 }), [stages, aov, cm]);
  const setS = (i, k) => (val) => setStages((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  return (
    <div data-testid="tool-D07" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Average order value (₹)" value={aov} onChange={setAov} testid="d07-aov" />
          <Field label="Contribution margin %" value={cm} onChange={setCm} testid="d07-cm" />
        </div>
        {stages.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 items-end border border-zinc-200 p-3" data-testid={`d07-stage-${i}`}>
            <div className="col-span-7"><input value={s.name} onChange={(e) => setS(i, "name")(e.target.value)} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black" /></div>
            <div className="col-span-5"><Field label="Monthly count" value={s.count} onChange={setS(i, "count")} testid={`d07-count-${i}`} /></div>
          </div>
        ))}
      </div>
      <div>
        <div data-testid="d07-result">
          <div className="space-y-2">
            {r.rows.slice(0, -1).map((row, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-3" data-testid={`d07-row-${i}`}>
                <span className="col-span-5 text-xs text-zinc-600 truncate">{row.name} → {stages[i + 1]?.name}</span>
                <div className="col-span-4 h-6 bg-zinc-100"><div className={`h-full ${row.name === r.weakestStage ? "bg-red-700" : "bg-zinc-500"}`} style={{ width: `${Math.max(3, row.convPct || 0)}%` }} /></div>
                <span className="col-span-3 text-right text-xs tabular-nums text-zinc-700">{row.convPct}%</span>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <ResultRow label="End-to-end conversion" value={`${r.endToEndPct}%`} />
            <ResultRow label="Contribution leaking / month" value={inr(r.totalLostValue)} strong tone="bad" />
            <ResultRow label="Biggest leak" value={r.weakestStage || "—"} />
            {r.whatIf && <ResultRow label={`+10% relative fix at "${r.whatIf.stage}"`} value={`+${inr(r.whatIf.extraValue)} / month`} tone="good" />}
          </div>
        </div>
        <ToolSection title="Evidence before fixes">
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
            <li>Session recordings on the leakiest step — watch ten before changing anything.</li>
            <li>Speed: measure LCP on the actual PDP template, on a mid-range phone, on 4G.</li>
            <li>Payment failure rates by gateway — often the largest invisible leak.</li>
          </ul>
        </ToolSection>
      </div>
    </div>
  );
}

/* ---------- D12 Pricing War Simulator ---------- */
export function ToolD12() {
  const [v, set] = useNumbers({ ourPrice: 1000, variableCost: 600, weeklyUnits: 100, compDropPct: 15, holdLossSensitivity: 0.8, bundleCostPerUnit: 50, bundleRetentionFactor: 0.5 });
  const r = useMemo(() => pricingWar(v), [v]);
  return (
    <div data-testid="tool-D12" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Your price (₹)" value={v.ourPrice} onChange={set("ourPrice")} testid="d12-price" />
          <Field label="Variable cost (₹)" value={v.variableCost} onChange={set("variableCost")} testid="d12-cost" />
          <Field label="Weekly units" value={v.weeklyUnits} onChange={set("weeklyUnits")} testid="d12-units" />
        </div>
        <SliderField label="Competitor price drop" value={v.compDropPct} onChange={set("compDropPct")} max={50} testid="d12-drop" />
        <SliderField label="Volume loss sensitivity" hint="% of units lost per 1% price disadvantage if you hold. 0.8 = typical for comparable products." value={v.holdLossSensitivity} onChange={set("holdLossSensitivity")} max={3} step={0.1} suffix="" testid="d12-sens" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Bundle/gift cost per unit (₹)" value={v.bundleCostPerUnit} onChange={set("bundleCostPerUnit")} testid="d12-bcost" />
          <Field label="Bundle retention factor" hint="1 = bundle saves all at-risk volume; 0.5 = half." value={v.bundleRetentionFactor} onChange={set("bundleRetentionFactor")} min={0} max={1} step={0.1} testid="d12-bfactor" />
        </div>
      </div>
      <div>
        <div data-testid="d12-result">
          <ResultRow label="Weekly contribution today" value={inr(r.base)} />
          {r.scenarios.map((s) => (
            <ResultRow key={s.label} label={`${s.label} — ${s.units} units`} value={`${inr(s.contribution)} (${s.vsBase >= 0 ? "+" : ""}${inr(s.vsBase)})`} strong={s.label === r.best} tone={s.label === r.best ? "good" : s.vsBase < -r.base * 0.25 ? "bad" : undefined} />
          ))}
          <ResultRow label="Best response" value={r.best} strong tone="good" />
        </div>
        {r.matchMarginNegative && <Note tone="warn">Matching takes you below variable cost — you'd be paying customers to take the product. Never match below contribution zero.</Note>}
        <Note tone="warn">The reversion risk: prices drop in a day and recover in a year. If you match, set the review date and the reversion trigger ('when rival returns to X, we return to Y') in the same meeting.</Note>
      </div>
    </div>
  );
}
